// #1152 N15-01: a Novig Game Detail intent travels in router state
// (`location.state.novigIntent`), never in the URL: `?venue=novig` is N15-02,
// deferred until producer identity. History state can hold anything, so the
// reader trusts only identity it can check against the live seam quote, and
// any mismatch returns null, which leaves today's Kalshi seed in place.
import type { NovigBoardQuote } from '@/lib/novig/presenter';
import type { TeamSide } from '@/views/ValueDashboard/types';

export interface NovigRouteIntentState {
  readonly novigIntent: { readonly v: 1; readonly boardKey: string; readonly outcomeId: string };
}

/** The router state a sender passes to open Game Detail on a Novig outcome. */
export function novigRouteState(boardKey: string, outcomeId: string): NovigRouteIntentState {
  return { novigIntent: { v: 1, boardKey, outcomeId } };
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * The Novig outcome to open, or null. The side is the quote slot that holds
 * the outcome, never a side the caller sends or the outcome's own field. Feed
 * state is not checked: a stale or withdrawn quote still opens the lane so its
 * reason shows.
 */
export function readNovigRouteIntent(
  state: unknown,
  gameKey: string,
  quote: NovigBoardQuote | null,
): { outcomeId: string; side: TeamSide } | null {
  if (!quote || !isRecord(state)) return null;
  const intent = state.novigIntent;
  if (!isRecord(intent) || intent.v !== 1) return null;
  if (typeof intent.boardKey !== 'string' || intent.boardKey !== gameKey) return null;
  if (typeof intent.outcomeId !== 'string') return null;
  for (const side of ['away', 'home'] as const) {
    if (quote[side]?.outcomeId === intent.outcomeId) return { outcomeId: intent.outcomeId, side };
  }
  return null;
}
